import React, { useState, useEffect, useCallback } from 'react'
import { brandCSS } from '../../brand'
import { HEADER_CSS } from './header.css.js'
import MegaMenuCruises from './MegaMenuCruises.jsx'

const base = (window.wpData?.siteUrl ?? '').replace(/\/$/, '')

// Fallback used only when no menu is assigned in WP Admin
const NAV_FALLBACK = [
  {
    label: 'Explore Cruises',
    megaMenu: true,
  },
  {
    label: 'Explore',
    children: [
      { label: 'Our Cruises',  href: `${base}/cruises` },
      { label: 'Itineraries',  href: `${base}/itineraries` },
      { label: 'Activities',   href: `${base}/activities` },
      { label: 'Destinations', href: `${base}/destinations` },
    ],
  },
  { label: 'Journal',        href: `${base}/journal` },
  { label: 'About',          href: `${base}/about` },
  { label: 'Special Offers', href: `${base}/special-offers` },
  { label: 'Contact Us',     href: `${base}/contact-us` },
]

const SITE_LOGO       = (window.wpData?.siteLogo      ?? '').trim()
const SITE_LOGO_LIGHT = (window.wpData?.siteLogoLight ?? '').trim()

function LogoSVG() {
  const src = SITE_LOGO_LIGHT || SITE_LOGO
  if (src) return <img src={src} alt="LJN Cruise" className="hd__logo-img" />
  return (
    <svg className="hd__logo-svg" width="140" height="44" viewBox="0 0 140 44" aria-label="LJN Cruise">
      <text x="0" y="30"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontSize="26" fontWeight="300" letterSpacing="6"
        fill="#F5F2EC">
        LJN
      </text>
      <text x="0" y="42"
        fontFamily="'DM Sans', system-ui, sans-serif"
        fontSize="7.5" fontWeight="400" letterSpacing="5"
        fill="rgba(201,165,106,0.8)">
        CRUISE &amp; TRAVEL
      </text>
    </svg>
  )
}

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  )
}

function ArrowRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  )
}

function NavItem({ item, currentPath, openDropdown, setOpenDropdown, scrolled }) {
  const hasChildren = item.children && item.children.length > 0
  const isOpen = openDropdown === item.id
  const isMega = item.megaMenu === true || item.label === 'Explore Cruises'
  const closeTimer = React.useRef(null)

  const megaOpen = () => {
    clearTimeout(closeTimer.current)
    setOpenDropdown(item.id)
  }
  const megaClose = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120)
  }

  return (
    <li
      className={`hd__nav-item${isMega ? ' hd__nav-item--mega' : ''}${hasChildren && isOpen ? ' hd__nav-item--open' : ''}${!hasChildren && currentPath.startsWith(item.href || '__') ? ' hd__nav-item--active' : ''}`}
      onMouseEnter={() => { if (hasChildren) setOpenDropdown(item.id); if (isMega) megaOpen() }}
      onMouseLeave={() => { if (hasChildren) setOpenDropdown(null); if (isMega) megaClose() }}
    >
      {(hasChildren || isMega) ? (
        <button
          className="hd__nav-link"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => setOpenDropdown(isOpen ? null : item.id)}
        >
          {item.label}
          <ChevronDown />
        </button>
      ) : (
        <a
          href={item.href}
          className="hd__nav-link"
          target={item.target || undefined}
          rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
        >
          {item.label}
        </a>
      )}

      {isMega && (
        <MegaMenuCruises
          open={isOpen}
          scrolled={scrolled}
          onMouseEnter={megaOpen}
          onMouseLeave={megaClose}
        />
      )}

      {!isMega && hasChildren && (
        <ul className="hd__dropdown" role="menu">
          {item.children.map((child) => {
            const hasGrandchildren = child.children && child.children.length > 0
            return hasGrandchildren ? (
              <li key={child.id ?? child.label} role="none" className="hd__dropdown-group">
                <span className="hd__dropdown-group-label">{child.label}</span>
                {child.children.map((grand) => (
                  <a
                    key={grand.id ?? grand.label}
                    href={grand.href}
                    className="hd__dropdown-link hd__dropdown-link--sub"
                    role="menuitem"
                    target={grand.target || undefined}
                    rel={grand.target === '_blank' ? 'noopener noreferrer' : undefined}
                  >
                    - {grand.label}
                  </a>
                ))}
              </li>
            ) : (
              <li key={child.id ?? child.label} role="none">
                <a
                  href={child.href}
                  className="hd__dropdown-link"
                  role="menuitem"
                  target={child.target || undefined}
                  rel={child.target === '_blank' ? 'noopener noreferrer' : undefined}
                >
                  {child.label}
                </a>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

export default function Header() {
  const [scrolled, setScrolled]         = useState(false)
  const [mobileOpen, setMobileOpen]     = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)

  // Use WP menu if available, else fallback
  const navItems = (window.wpData?.menu?.length > 0)
    ? window.wpData.menu
    : NAV_FALLBACK

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const closeMobile = useCallback(() => setMobileOpen(false), [])
  const currentPath = window.location.pathname

  return (
    <>
      <style>{brandCSS + HEADER_CSS}</style>

      <header className={`hd ${scrolled ? 'hd--solid' : 'hd--transparent'}`} role="banner">
        <div className="hd__accent" aria-hidden="true" />

        <div className="hd__inner">
          <a href={window.wpData?.siteUrl ?? '/'} className="hd__logo" aria-label="LJN Cruise — Home">
            <LogoSVG />
          </a>

          <nav aria-label="Main navigation">
            <ul className="hd__nav">
              {navItems.map((item) => (
                <NavItem
                  key={item.id ?? item.label}
                  item={item}
                  currentPath={currentPath}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  scrolled={scrolled}
                />
              ))}
            </ul>
          </nav>

          <div className="hd__right">
            <a href={`${base}/booking`} className="hd__cta">
              Book Now <ArrowRight />
            </a>
            <button
              className={`hd__hamburger${mobileOpen ? ' hd__hamburger--open' : ''}`}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="hd-mobile-menu"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <span className="hd__hamburger-bar" />
              <span className="hd__hamburger-bar" />
              <span className="hd__hamburger-bar" />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`hd__mobile-overlay${mobileOpen ? ' hd__mobile-overlay--open' : ''}`}
        aria-hidden="true"
        onClick={closeMobile}
      />

      {/* Nav Mobile Menu */}
      <nav
        id="hd-mobile-menu"
        className={`hd__mobile${mobileOpen ? ' hd__mobile--open' : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!mobileOpen}
        inert={!mobileOpen ? '' : undefined}
      >
        <button
          className="hd__mobile-close"
          onClick={closeMobile}
          aria-label="Close menu"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>

        <ul className="hd__mobile-nav">
          {navItems.map((item) => (
            <React.Fragment key={item.id ?? item.label}>
              {item.children && item.children.length > 0 ? (
                <>
                  <li>
                    <span className="hd__mobile-section">{item.label}</span>
                  </li>
                  {item.children.map((child, ci) => {
                    const hasGrandchildren = child.children && child.children.length > 0
                    const isLastChild = ci === item.children.length - 1
                    return hasGrandchildren ? (
                      <React.Fragment key={child.id ?? child.label}>
                        <li>
                          <span className="hd__mobile-section hd__mobile-section--sub">{child.label}</span>
                        </li>
                        {child.children.map((grand, gi) => {
                          const isLastGrand = isLastChild && gi === child.children.length - 1
                          return (
                            <li key={grand.id ?? grand.label}>
                              <a
                                href={grand.href}
                                className={`hd__mobile-link hd__mobile-link--child hd__mobile-link--grand${isLastGrand ? ' hd__mobile-link--child-last' : ''}`}
                                onClick={closeMobile}
                              >
                                {grand.label}
                              </a>
                            </li>
                          )
                        })}
                      </React.Fragment>
                    ) : (
                      <li key={child.id ?? child.label}>
                        <a
                          href={child.href}
                          className={`hd__mobile-link hd__mobile-link--child${isLastChild ? ' hd__mobile-link--child-last' : ''}`}
                          onClick={closeMobile}
                        >
                          {child.label}
                        </a>
                      </li>
                    )
                  })}
                  <li className="hd__mobile-separator" aria-hidden="true" />
                </>
              ) : (
                <li>
                  <a href={item.href} className="hd__mobile-link" onClick={closeMobile}>
                    {item.label}
                  </a>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>

        <a href={`${base}/contact-us`} className="hd__mobile-cta" onClick={closeMobile}>
          Book Now <ArrowRight />
        </a>
      </nav>
    </>
  )
}
